import { useMemo, useState } from "react";
import moment from "moment";

import { weekDays } from "../sampleData";
import ProductPhoto from "../components/ProductPhoto";
import { pctLabel } from "../commission";
import "../review-order.css";

/* ------------------------------------------------------------------ *
 * Review & confirm — step 3 of /new-order, for both order types.
 *
 * Replaces OverLayInputSummary on the redesigned entry page:
 *   - a modal over the form        -> a full step-3 view
 *   - one flat product table       -> Table / Cards toggle, cards with photos
 *   - silent missing-field state   -> a pre-confirm check banner
 *   - "Submit"                     -> "Confirm order" + an On confirm panel
 *
 * Not a routed page: newOrderPage owns the draft in local state and swaps
 * this view in when the user reaches Review, so the draft survives the
 * round trip back to the form by construction. All figures are derived
 * from the same state the POST body is built from — the review can never
 * disagree with what confirming submits.
 * ------------------------------------------------------------------ */

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const TYPE_COLORS = {
  "Casket cover": "var(--rf-brand)",
  Wreath: "var(--rf-brand-lite)",
  Bowl: "var(--rf-brand-soft)",
  Heart: "var(--rf-brand-pale)",
  Sprays: "var(--rf-brand-soft)",
  Cross: "var(--rf-brand-lite)",
  Letters: "var(--rf-brand-pale)",
  "Singles/Petals": "#ebdcbf",
};

/* Locations are stored as JSON { name } but old rows are bare strings. */
function locationName(value) {
  if (!value) return "";
  try {
    return JSON.parse(value).name || "";
  } catch (err) {
    return value;
  }
}

function longDate(ddmmyyyy) {
  if (!ddmmyyyy) return "";
  const m = moment(ddmmyyyy, "DD/MM/YYYY");
  return m.isValid() ? m.format("ddd D MMM YYYY") : ddmmyyyy;
}

function weekdayOf(ddmmyyyy) {
  if (!ddmmyyyy) return "";
  const m = moment(ddmmyyyy, "DD/MM/YYYY");
  return m.isValid() ? m.format("dddd") : "";
}

function ProductTable({ lines }) {
  return (
    <>
      <div className="rv-thead">
        <span className="rv-col-product">Product</span>
        <span className="rv-col-dest">Deliver to</span>
        <span className="rv-col-qty">Qty</span>
        <span className="rv-col-unit">Unit</span>
        <span className="rv-col-total">Line total</span>
      </div>

      {lines.map((l) => (
        <div key={l.key} className="rv-trow">
          <div className="rv-col-product rv-product-cell">
            <span className="rv-accent" style={{ background: l.color }} />
            <div className="rv-product-text">
              <div className="rv-product-name">{l.name}</div>
              <div className="rv-product-meta">{l.meta}</div>
            </div>
          </div>
          <span className="rv-col-dest">{l.dest}</span>
          <span className="rv-col-qty rf-num">×{l.amount}</span>
          <span className="rv-col-unit rf-num">
            {money(l.unit)}
            {l.sashPrice ? ` +${money(l.sashPrice)}` : ""}
          </span>
          <span className="rv-col-total rf-num">{money(l.total)}</span>
        </div>
      ))}
    </>
  );
}

function ProductCards({ lines }) {
  return (
    <div className="rv-cards">
      {lines.map((l) => (
        <div key={l.key} className="rv-card">
          <div className="rv-card-photo">
            {l.image ? (
              <ProductPhoto product={l} size="thumb" guess />
            ) : (
              <span>{l.type}</span>
            )}
          </div>

          <div className="rv-card-body">
            <div className="rv-card-top">
              <span className="rv-accent" style={{ background: l.color }} />
              <div className="rv-card-text">
                <div className="rv-card-name">{l.name}</div>
                <div className="rv-card-meta">{l.meta}</div>
              </div>
              <span className="rv-card-qty rf-num">×{l.amount}</span>
            </div>

            <div className="rv-card-chips">
              {l.dest && <span className="rv-chip is-dest">{l.dest}</span>}
              {l.sashText && (
                <span className="rv-chip is-sash">Sash “{l.sashText}”</span>
              )}
              {l.benchNote && (
                <span className="rv-chip is-note">{l.benchNote}</span>
              )}
            </div>

            <div className="rv-card-foot">
              <span className="rv-card-unit rf-num">
                {money(l.unit)} each
                {l.sashPrice ? ` · sash ${money(l.sashPrice)}` : ""}
              </span>
              <span className="rv-card-total rf-num">{money(l.total)}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* Everything the banner warns about is derived, never stored — so it can
   never disagree with the order it's describing. */
function buildChecks({ isRetail, order, recipient, lines, feeValue }) {
  const checks = [];
  if (!order.invoice_number) {
    checks.push("No invoice number — the order will show as UNPAID");
  }
  const blankSash = lines.filter((l) => l.sashPrice && !l.sashText);
  if (blankSash.length) {
    checks.push(
      `${blankSash[0].name} has a sash with no message typed`
    );
  }
  if (!feeValue) {
    checks.push("No delivery fee selected");
  }
  if (!isRetail && lines.some((l) => l.type === "Casket cover")) {
    checks.push("Casket cover cut-off is 90 minutes before the service");
  }
  if (isRetail && !recipient.recipient_name && !order.company_name) {
    checks.push("No recipient or company entered");
  }
  return checks;
}

export function ReviewOrder({
  createIn, // another business's name when the order goes there, else ""
  orderType,
  orderDetail,
  selectedProducts,
  selectedTribuites,
  deliveryFee,
  currentUser,
  totals, // { productsTotal, exGst, gstAmount, gstRate, feeValue, grandTotal }
  commission, // Funeral only: { apply, pct, amount } — null on Retail
  busy,
  isEdit, // editing a saved order: nothing new is issued on confirm
  onBack,
  onConfirm,
}) {
  const [view, setView] = useState("Table");
  const isRetail = orderType === "Retail";

  const order = orderDetail.order || {};
  const datetime = orderDetail.datetime || {};
  const recipient = orderDetail.recipient || {};
  const fd = (orderDetail.fd_code || {})["FD Code"] || {};

  const ccDest = locationName(datetime.casket_cover_delivery_location);
  const triDest = locationName(datetime.tribuite_delivery_location);
  const retailDest = locationName(recipient.delivery_location);

  /* The same line objects the POST body carries, decorated for display.
     unit and total use the repo's pricing rule: custom price wins, sash
     is charged once per line. */
  const lines = useMemo(() => {
    const source = isRetail
      ? selectedProducts
      : [...selectedProducts, ...selectedTribuites];
    return source
      .filter((l) => !l.deleted)
      .map((l, i) => {
        const unit = Number(l.customPrice ? l.customPrice : l.price) || 0;
        const sashPrice = Number(l.sashPrice) || 0;
        const isCasket = l.product_type === "Casket cover";
        const dest = isRetail
          ? retailDest
          : isCasket
          ? ccDest
          : triDest;
        return {
          key: `${l._id || l.code}-${i}`,
          name: l.product_name,
          type: l.product_type || "",
          image: l.image || null,
          image_variants: l.image_variants,
          color: TYPE_COLORS[l.product_type] || "var(--rf-brand-soft)",
          meta: [l.code, l.size, l.lidType].filter(Boolean).join(" · "),
          dest,
          amount: Number(l.amount) || 1,
          unit,
          sashPrice,
          sashText: l.sash_message || "",
          benchNote: l.comment || "",
          total: unit * (Number(l.amount) || 1) + sashPrice,
        };
      });
  }, [isRetail, selectedProducts, selectedTribuites, ccDest, triDest, retailDest]);

  const itemCount = lines.reduce((n, l) => n + l.amount, 0);

  /* Cheap enough to recompute every render — memoising it would only pin
     it to object identities that change each render anyway. */
  const checks = buildChecks({
    isRetail,
    order,
    recipient,
    lines,
    feeValue: totals.feeValue,
  });

  const personName = isRetail
    ? order.company_name || recipient.recipient_name || "New order"
    : order.late_person_name || "New order";

  const receivedBy =
    (currentUser || {}).full_name ||
    (currentUser || {}).name ||
    (currentUser || {}).username ||
    "—";

  const paymentLine = [
    order.payment_option || "No payment method",
    order.payment_date ? longDate(order.payment_date) : "",
    order.payment_email || "",
  ]
    .filter(Boolean)
    .join(" · ");

  /* ---------- fact cards, per type ---------- */
  const scheduleValue = isRetail
    ? recipient.frequency === "Once"
      ? `One-time · ${longDate(recipient.delivery_date)}`
      : [
          recipient.frequency,
          weekDays[recipient.delivery_day],
          recipient.order_start_date
            ? `starts ${longDate(recipient.order_start_date)}`
            : "",
        ]
          .filter(Boolean)
          .join(" · ")
    : "";

  const orderRows = [
    isRetail
      ? { k: "Company", v: order.company_name || "—" }
      : {
          k: "Funeral director",
          v: fd.business_name
            ? `${fd.business_name}${fd.code ? ` (${fd.code})` : ""}`
            : fd.code || "—",
        },
    ...(!isRetail && commission
      ? [
          {
            k: "FD commission",
            v: commission.apply
              ? `Applied at ${pctLabel(commission.pct)} — about ${money(
                  commission.amount
                )} of this order`
              : "Not applied",
          },
        ]
      : []),
    {
      k: "Ordered by",
      v: [order.ordered_by, order.orderer_phone].filter(Boolean).join(" · ") ||
        "—",
    },
    {
      k: "Payment",
      v: paymentLine,
      tone: order.payment_option === "**UNPAID**" ? "danger" : "",
    },
    {
      k: "Invoice",
      v: order.invoice_number || "Not entered — order will show as UNPAID",
      tone: order.invoice_number ? "" : "danger",
    },
    { k: "Received by", v: receivedBy },
  ];

  const deliveryRows = isRetail
    ? [
        {
          k: "Recipient",
          v:
            [recipient.recipient_name, recipient.recipient_phone]
              .filter(Boolean)
              .join(" · ") || "—",
        },
        {
          k: "Deliver to",
          v:
            [retailDest, recipient.delivery_area].filter(Boolean).join(" · ") ||
            "—",
        },
        { k: "Delivery time", v: recipient.time_of_delivery || "—" },
        { k: "Schedule", v: scheduleValue || "—" },
        /* no "Authority to leave" row — it is standard now, always granted */
        ...(order.card_message
          ? [{ k: "Card message", v: `“${order.card_message}”` }]
          : []),
        {
          k: "Driver",
          v: "Assigned when the run sheet is built",
          tone: "muted",
        },
      ]
    : [
        {
          k: "Service",
          v:
            [longDate(datetime.funeral_date), datetime.funeral_time]
              .filter(Boolean)
              .join(" · ") || "—",
        },
        { k: "Location", v: locationName(datetime.funeral_location) || "—" },
        {
          k: "Casket cover to",
          v: ccDest
            ? `${ccDest}${
                datetime.casket_cover_delivery_time
                  ? ` — by ${datetime.casket_cover_delivery_time}`
                  : ""
              }`
            : "—",
        },
        {
          k: "Tributes to",
          v: triDest
            ? `${triDest}${
                datetime.tributes_delivery_time
                  ? ` — by ${datetime.tributes_delivery_time}`
                  : ""
              }`
            : "—",
        },
        {
          k: "Driver",
          v: "Assigned when the run sheet is built",
          tone: "muted",
        },
      ];

  const sections = [
    { title: "Order & payment", rows: orderRows },
    {
      title: isRetail ? "Recipient & delivery" : "Service, times & delivery",
      rows: deliveryRows,
    },
  ];

  /* ---------- the On confirm panel ---------- */
  const listDay = isRetail
    ? recipient.frequency === "Once"
      ? weekdayOf(recipient.delivery_date)
      : weekDays[recipient.delivery_day] || ""
    : weekdayOf(datetime.funeral_date);

  const effects = [
    isEdit
      ? `The saved order is updated on ${listDay ? `${listDay}'s` : "the"} ${
          isRetail ? "retail " : ""
        }work order list — its serial number stays the same`
      : `A serial number is issued and the order appears on ${
          listDay ? `${listDay}'s` : "the"
        } ${isRetail ? "retail " : ""}work order list`,
    isEdit
      ? `The order carries ${itemCount} product${
          itemCount === 1 ? "" : "s"
        }; added products join the production queue, removed ones leave it`
      : `${itemCount} product${itemCount === 1 ? " drops" : "s drop"} into the production queue as “Not started”`,
    ...(!isRetail && lines.some((l) => l.type === "Casket cover")
      ? [
          `Casket cover is flagged for delivery${
            datetime.casket_cover_delivery_time
              ? ` by ${datetime.casket_cover_delivery_time}`
              : ""
          }`,
        ]
      : []),
    ...(isRetail && recipient.frequency && recipient.frequency !== "Once"
      ? [
          `The order repeats ${(recipient.frequency || "").toLowerCase()} until it is cancelled`,
        ]
      : []),
    order.payment_option === "Account"
      ? `${
          isRetail
            ? order.company_name || "The account"
            : fd.business_name || "The director's account"
        } is invoiced ${money(totals.grandTotal)}`
      : `Order total is ${money(totals.grandTotal)} incl. GST and delivery`,
  ];

  const subtitle = [
    isRetail
      ? scheduleValue
      : [longDate(datetime.funeral_date), datetime.funeral_time]
          .filter(Boolean)
          .join(" "),
    isRetail
      ? recipient.recipient_name && order.company_name
        ? `for ${recipient.recipient_name}`
        : ""
      : fd.business_name
      ? `${fd.business_name}${fd.code ? ` (${fd.code})` : ""}`
      : "",
    `${itemCount} product${itemCount === 1 ? "" : "s"}`,
    money(totals.productsTotal),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="rv-page">
      {/* ---------- step bar ---------- */}
      <div className="rf-steps">
        {["Details", "Products", "Review"].map((label, i) => {
          const n = i + 1;
          const state = n < 3 ? "done" : "active";
          return (
            <button
              key={label}
              type="button"
              className={`rf-step is-${state}`}
              onClick={() => n < 3 && onBack()}
            >
              <span className="rf-step-badge">
                {state === "done" ? "✓" : n}
              </span>
              <span className="rf-step-label">{label}</span>
              <span className="rf-step-line" />
            </button>
          );
        })}
        <span className="rf-spacer" />
        <span className="rf-steps-meta">Nothing is saved until you confirm</span>
      </div>

      <div className="rv-body">
        {/* ---------- main column ---------- */}
        <div className="rv-main">
          <header className="rv-head">
            <div className="rf-label">
              Review new {orderType.toLowerCase()} order
            </div>
            <h1 className="rf-serif rv-title">{personName}</h1>
            <div className="rv-subtitle">{subtitle}</div>
            {createIn && (
              <div className="rv-business">
                Will be created in <b>{createIn}</b>
              </div>
            )}
          </header>

          {checks.length > 0 && (
            <div className="rv-banner">
              <span className="rv-banner-bar" />
              <div className="rv-banner-text">
                <div className="rv-banner-title">
                  {checks.length} thing{checks.length > 1 ? "s" : ""} to check
                  before you confirm
                </div>
                <div className="rv-banner-list">{checks.join(" · ")}</div>
              </div>
              <button type="button" className="rf-link" onClick={onBack}>
                Fix now
              </button>
            </div>
          )}

          <div className="rv-sections">
            {sections.map((sec) => (
              <section key={sec.title} className="rv-section">
                <div className="rv-section-head">
                  <span className="rv-section-title">{sec.title}</span>
                  <button type="button" className="rf-link" onClick={onBack}>
                    Edit
                  </button>
                </div>
                {sec.rows.map((r) => (
                  <div key={r.k} className="rv-fact">
                    <span className="rv-fact-k">{r.k}</span>
                    <span className={`rv-fact-v${r.tone ? ` is-${r.tone}` : ""}`}>
                      {r.v}
                    </span>
                  </div>
                ))}
              </section>
            ))}
          </div>

          <section className="rv-products">
            <div className="rv-section-head">
              <span className="rv-section-title">
                Products · {lines.length} item{lines.length === 1 ? "" : "s"}
              </span>
              <div className="rf-segmented rv-viewtoggle">
                {["Table", "Cards"].map((v) => (
                  <button
                    key={v}
                    type="button"
                    className={view === v ? "is-active" : ""}
                    onClick={() => setView(v)}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>

            {view === "Table" ? (
              <ProductTable lines={lines} />
            ) : (
              <ProductCards lines={lines} />
            )}
          </section>
        </div>

        {/* ---------- confirm rail ---------- */}
        <aside className="rv-rail">
          <div className="rv-total-card">
            <div className="rv-total-label">Order total</div>
            {[
              {
                k: `Products (${lines.length}) ex GST`,
                v: money(totals.exGst),
              },
              {
                k: `GST (${Math.round(totals.gstRate * 100)}%)`,
                v: money(totals.gstAmount),
              },
              { k: "Delivery fee", v: money(totals.feeValue) },
            ].map((t) => (
              <div key={t.k} className="rv-total-row">
                <span>{t.k}</span>
                <span>{t.v}</span>
              </div>
            ))}
            <div className="rv-total-row is-total">
              <span>Total incl. GST</span>
              <span>{money(totals.grandTotal)}</span>
            </div>
            {!isRetail && commission && commission.apply && (
              <>
                <div className="rv-total-row is-commission">
                  <span>Director commission ({pctLabel(commission.pct)})</span>
                  <span>{money(commission.amount)}</span>
                </div>
                <div className="rv-total-row is-net">
                  <span>Total after commission</span>
                  <span>{money(totals.grandTotal - commission.amount)}</span>
                </div>
              </>
            )}

            <div className="rv-payment">
              <div className="rv-payment-label">Payment</div>
              <div className="rv-payment-value">
                {order.payment_option
                  ? `${order.payment_option}${
                      order.payment_date
                        ? ` · paid ${longDate(order.payment_date)}`
                        : ""
                    }`
                  : "No payment method selected"}
              </div>
            </div>
          </div>

          <div className="rv-effects">
            <div className="rv-effects-title">On confirm</div>
            {effects.map((e) => (
              <div key={e} className="rv-effect">
                <span className="rv-effect-dot" />
                <span>{e}</span>
              </div>
            ))}
          </div>

          <div className="rv-actions">
            <button
              type="button"
              className="rf-btn rf-btn-primary rv-confirm"
              onClick={() => !busy && onConfirm()}
              disabled={busy}
            >
              {busy ? "Saving…" : isEdit ? "Save changes" : "Confirm order"}
            </button>
            <button
              type="button"
              className="rf-btn rf-btn-ghost rv-back"
              onClick={onBack}
            >
              ← Back to details
            </button>
            <div className="rv-serial-note">
              Serial number is issued on confirm
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default ReviewOrder;
